import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { FALLBACK_LANGUAGE, LANGUAGES, TRANSLATION_NAMESPACE, } from '@citylytics/domain/languages/constants';
import { resolveLanguage } from '@citylytics/domain/languages/utils';
const KNOWN_LANGUAGES = LANGUAGES;
function isKnownLanguage(value) {
    return KNOWN_LANGUAGES.includes(value);
}
/**
 * The languages the app registered with `setupWebI18n`, read back from i18next rather than kept
 * here, so this package holds no state of its own. i18next appends its own `cimode`, which is
 * no language of ours, so only domain's languages are kept.
 */
export function readAppLanguages() {
    const supported = i18n.options.supportedLngs;
    return Array.isArray(supported) ? supported.filter(isKnownLanguage) : [FALLBACK_LANGUAGE];
}
/** The app's language i18next is showing; what core-api is asked to answer in. */
export function readCurrentLanguage() {
    return resolveLanguage({
        requested: i18n.resolvedLanguage ?? i18n.language,
        supported: readAppLanguages(),
    });
}
/** Calls `onChange` whenever the language switches; returns the unsubscribe, for React. */
export function subscribeToLanguage(onChange) {
    i18n.on('languageChanged', onChange);
    return () => i18n.off('languageChanged', onChange);
}
/**
 * Whether this app ships the language. The apps don't all speak the same ones (client-web has
 * Danish, admin-web doesn't), and the account's language is shared between them.
 */
export function isSpokenHere(language) {
    return readAppLanguages().includes(language);
}
function readDeviceStorageKey() {
    const key = i18n.options.detection?.lookupLocalStorage;
    if (typeof key !== 'string') {
        throw new Error('setupWebI18n has not run: no localStorage key for the device language');
    }
    return key;
}
/**
 * The device's language: the one last chosen in this browser while signed out, else the first of
 * the browser's own that the app speaks, else the fallback. Read fresh from the detector rather
 * than from what is showing, since the account's language may be showing instead.
 */
function readDeviceLanguage() {
    const detected = i18n.services.languageDetector?.detect();
    const candidates = Array.isArray(detected) ? detected : [detected];
    const supported = readAppLanguages();
    for (const candidate of candidates) {
        const resolved = resolveLanguage({ requested: candidate, supported });
        // `resolveLanguage` answers the fallback for a language it doesn't know; skip those.
        if (candidate?.toLowerCase().startsWith(resolved))
            return resolved;
    }
    return FALLBACK_LANGUAGE;
}
/** A choice made on this device (signed out, or on the error page): shown and remembered here. */
export function changeDeviceLanguage(language) {
    localStorage.setItem(readDeviceStorageKey(), language);
    void i18n.changeLanguage(language);
}
/**
 * Shows the language saved on the signed-in user's account, without remembering it as the
 * device's choice. Null ("As on device"), or a language this app doesn't speak, shows the
 * device's language instead of falling back to English.
 */
export function applySavedLanguage(saved) {
    const language = saved !== null && isSpokenHere(saved) ? saved : readDeviceLanguage();
    if (language !== readCurrentLanguage()) {
        void i18n.changeLanguage(language);
    }
}
/**
 * Starts i18next for a web app with the app's own languages and translations: the language last
 * chosen in this browser (else the browser's own), and `<html lang>` kept in step so screen
 * readers and spellcheck follow a switch. Run once, before rendering.
 */
export function setupWebI18n(params) {
    const { languages, translations, localStorageKey } = params;
    void i18n
        .use(LanguageDetector)
        .use(initReactI18next)
        .init({
        resources: Object.fromEntries(languages.map((language) => [
            language,
            { [TRANSLATION_NAMESPACE]: translations[language] },
        ])),
        defaultNS: TRANSLATION_NAMESPACE,
        fallbackLng: FALLBACK_LANGUAGE,
        supportedLngs: [...languages],
        nonExplicitSupportedLngs: true,
        interpolation: {
            escapeValue: false,
        },
        detection: {
            order: ['localStorage', 'navigator'],
            // Not cached on every switch: the account's language is shown too, and caching it would
            // make it the device's choice, so "As on device" could never go back. Device choices are
            // stored by `changeDeviceLanguage`.
            caches: [],
            lookupLocalStorage: localStorageKey,
        },
    });
    i18n.on('languageChanged', (language) => {
        document.documentElement.lang = language;
    });
    document.documentElement.lang = i18n.resolvedLanguage ?? i18n.language;
}
